import { ChirpConfigs, CustomClaimValue, Icons, OperationMode, RadarClassificationMode, RadarConfiguration, SensorConfigType, SensorConfiguration, TrackerConfigs, TrackerConfigType, useAuth } from "@app/shared";
import { ChangeEventHandler, useEffect, useState } from "react";
import { useFetch, useModel } from "../../hooks";
import { HeaderWithSeparator } from "./HeaderWithSeparator";
import { HoverTooltip } from "./HoverTooltip";
import { IconLink } from "./IconLink";
import { CheckboxInput, Input, InputRow, Select, SelectOption, TextArea } from "./inputs";

const emptyGuid = "00000000-0000-0000-0000-000000000000";

// Mirrors Shared/Helpers/ChirpConfigText.HasCommands: the radar skips blank and "%" comment lines, so
// text made only of those configures nothing.
const hasChirpCommands = (chirpConfig?: string | null) =>
    (chirpConfig ?? "").split("\n").some((line) => {
        const trimmed = line.trim();
        return trimmed.length > 0 && !trimmed.startsWith("%");
    });

type EditSensorConfigProps = {
    config: SensorConfiguration | RadarConfiguration;
    deviceMode?: OperationMode;
    onChange: (newVal: Partial<SensorConfiguration> | Partial<RadarConfiguration>) => void;
    /** Called with a message while the config can't be saved, and with undefined once it can. */
    onValidityChange?: (error: string | undefined) => void;
    /**
     * Whether "Custom Config" was explicitly picked in this edit. Owned by the parent because this
     * component unmounts when the user switches sections, and the pick must survive that.
     */
    customChirpChosen?: boolean;
    onCustomChirpChosenChange?: (chosen: boolean) => void;
};

export const EditSensorConfig: React.FC<EditSensorConfigProps> = ({ config, onChange, deviceMode, onValidityChange, customChirpChosen = false, onCustomChirpChosenChange }) => {
    const user = useAuth();
    const isSuperAdmin = user.userClaims?.find((uc) => uc.claims.find((c) => c === CustomClaimValue.SuperAdmin));

    const [radarConfig, radarHelpers] = useModel(RadarConfiguration, config);

    //CHIRP configs and options
    const [freezeInfoOpen, setFreezeInfoOpen] = useState(false);

    const [chirpConfigs, , chirpConfigsApi] = useFetch(() => ChirpConfigs.getByMode(deviceMode ?? OperationMode.Intersection_Management), [deviceMode]);
    const [chirpConfigOptions, setChirpConfigOptions] = useState<SelectOption[]>([]);
    // Until the options exist the select can't show the configured preset, and the browser falls back
    // to the empty "Device Default" option — which then visibly jumps to the real value. Show
    // "loading..." (no empty option) until they're built.
    const [chirpOptionsReady, setChirpOptionsReady] = useState(false);
    // No preset and no usable text is "device default" (the empty option), not a blank Custom — they used
    // to render identically, which is how a device ended up "Custom" with nothing in it. customChirpChosen
    // keeps an explicit Custom pick across this component unmounting when the user switches sections.
    const [newChirpConfigId, setNewChirpConfigId] = useState<string | undefined>(
        radarConfig.chirpConfigId ?? (customChirpChosen || hasChirpCommands(radarConfig.chirpConfig) ? emptyGuid : "")
    );

    //Tracker Configs and options
    const [trackerConfigs, , trackerConfigApi] = useFetch(() => TrackerConfigs.getAll());
    const [videoTrackerConfigOptions, setVideoTrackerConfigOptions] = useState<SelectOption[]>([]);
    const [radarTrackerConfigOptions, setRadarTrackerConfigOptions] = useState<SelectOption[]>([]);
    const [trackerOptionsReady, setTrackerOptionsReady] = useState(false);
    const [newVideoTrackerConfigId, setNewVideoTrackerConfigId] = useState<string | undefined>(radarConfig.videoTrackerConfigId ?? ((radarConfig.videoTrackerConfig ?? "").length > 0 ? emptyGuid : ""));
    const [newRadarTrackerConfigId, setNewRadarTrackerConfigId] = useState<string | undefined>(radarConfig.radarTrackerConfigId ?? ((radarConfig.radarTrackerConfig ?? "").length > 0 ? emptyGuid : ""));

    const getChirpConfigDescription = (radarConfig: Partial<RadarConfiguration>) => {
        if ((radarConfig.chirpConfig ?? "").length === 0) return "No Config - Use Device Default";
        if (!radarConfig.chirpConfigId || radarConfig.chirpConfigId === emptyGuid) return "Custom Config";

        const configDetails = radarConfig.chirpConfigPreset ?? chirpConfigs?.find((c) => c.id === radarConfig.chirpConfigId);
        if (!configDetails) return radarConfig.chirpConfigId;
        return (
            <>
                <i>{configDetails.description}</i>
            </>
        );
    };

    const getTrackerConfigDescription = (type: "radar" | "video") => {
        const trackerConfig = type === "radar" ? radarConfig.radarTrackerConfig : radarConfig.videoTrackerConfig;
        const trackerConfigId = type === "radar" ? radarConfig.radarTrackerConfigId : radarConfig.videoTrackerConfigId;
        const trackerConfigPreset = type === "radar" ? radarConfig.radarTrackerConfigPreset : radarConfig.videoTrackerConfigPreset;

        // Custom / no config: the select already says so. Only a preset has a description to add.
        if ((trackerConfig ?? "").length === 0) return null;
        if (!trackerConfigId || trackerConfigId === emptyGuid) return null;

        const configDetails = trackerConfigPreset ?? trackerConfigs?.find((c) => c.id === trackerConfigId);
        if (!configDetails) return trackerConfigId;
        return (
            <>
                <i>{configDetails.description}</i>
            </>
        );
    };

    const trackerConfigMatches = (type: "radar" | "video") => {
        const trackerConfigId = type === "radar" ? radarConfig.radarTrackerConfigId : radarConfig.videoTrackerConfigId;
        const associatedConfigId = type === "radar" ? radarConfig.chirpConfigPreset?.radarTrackerConfigId : radarConfig.chirpConfigPreset?.videoTrackerConfigId;

        if (associatedConfigId === undefined || associatedConfigId === null || associatedConfigId === emptyGuid) return true; // no associated config to match against

        return associatedConfigId === trackerConfigId;
    };

    //If our props change, update our helper model
    useEffect(() => {
        radarHelpers.replaceModel(config as RadarConfiguration);
    }, [config]);

    // Refresh data if the device mode changes
    useEffect(() => {
        chirpConfigsApi.refreshData();
    }, [deviceMode]);

    //Setup options when the chirp configs change
    useEffect(() => {
        if (chirpConfigsApi.isLoading) return;
        var options = [...(chirpConfigs ?? []).map((config) => ({ label: config.name, value: config.id } as SelectOption))];
        if (isSuperAdmin || !radarConfig.chirpConfigId || radarConfig.chirpConfigId === emptyGuid) {
            // only super can select this if it's not already set.
            options = [{ label: "Custom Config", value: emptyGuid, disabled: !isSuperAdmin }, ...options];
        }
        setChirpConfigOptions(options);
        setChirpOptionsReady(true);
    }, [chirpConfigs, chirpConfigsApi.isLoading, isSuperAdmin, radarConfig.chirpConfigId]);

    //Setup options when the tracker configs change
    useEffect(() => {
        if (trackerConfigApi.isLoading) return;

        //Video options
        var videoOptions = [...(trackerConfigs ?? []).filter((tc) => tc.type === TrackerConfigType.Video).map((config) => ({ label: config.name, value: config.id } as SelectOption))];
        if (isSuperAdmin || !radarConfig.videoTrackerConfigId || radarConfig.videoTrackerConfigId === emptyGuid) {
            // only super can select this if it's not already set.
            videoOptions = [{ label: "Custom Config", value: emptyGuid, disabled: !isSuperAdmin }, ...videoOptions];
        }
        setVideoTrackerConfigOptions(videoOptions);

        //Radar options
        var radarOptions = [...(trackerConfigs ?? []).filter((tc) => tc.type === TrackerConfigType.Radar).map((config) => ({ label: config.name, value: config.id } as SelectOption))];
        if (isSuperAdmin || !radarConfig.radarTrackerConfigId || radarConfig.radarTrackerConfigId === emptyGuid) {
            // only super can select this if it's not already set.
            radarOptions = [{ label: "Custom Config", value: emptyGuid, disabled: !isSuperAdmin }, ...radarOptions];
        }
        setRadarTrackerConfigOptions(radarOptions);
        setTrackerOptionsReady(true);
    }, [trackerConfigs, trackerConfigApi.isLoading, isSuperAdmin, radarConfig.videoTrackerConfigId, radarConfig.radarTrackerConfigId]);

    // Custom means "send this text", so it must have some. For the device default pick the empty option.
    const chirpConfigError =
        config.configType === SensorConfigType.Radar && newChirpConfigId === emptyGuid && !hasChirpCommands(radarConfig.chirpConfig)
            ? "The custom CHIRP config is blank. Enter a config, or choose a preset or Device Default."
            : undefined;

    useEffect(() => {
        onValidityChange?.(chirpConfigError);
    }, [chirpConfigError, onValidityChange]);

    const handleChirpConfigOnChange: ChangeEventHandler<HTMLSelectElement> = async (e) => {
        setNewChirpConfigId(e.target.value ?? "");
        onCustomChirpChosenChange?.(e.target.value === emptyGuid);
        const newPreset = chirpConfigs?.find((c) => c.id === e.target.value);
        const chirpConfig = e.target.value === emptyGuid ? radarConfig.chirpConfig : newPreset?.chirpConfig ?? "";
        onChange({ ...radarConfig, chirpConfigId: e.target.value === emptyGuid || !e.target.value ? undefined : e.target.value, chirpConfigPreset: newPreset, chirpConfig: chirpConfig } as Partial<RadarConfiguration>);
    };

    const updateVideoTrackerConfig = (trackerConfigId: string) => {
        const newPreset = trackerConfigs?.find((c) => c.id === trackerConfigId);
        const videoTrackerConfig = trackerConfigId === emptyGuid ? radarConfig.videoTrackerConfig : newPreset?.trackerConfig ?? "";
        onChange({ ...radarConfig, videoTrackerConfigId: trackerConfigId === emptyGuid || !trackerConfigId ? undefined : trackerConfigId, videoTrackerConfigPreset: newPreset, videoTrackerConfig: videoTrackerConfig } as Partial<RadarConfiguration>);
    };

    const updateRadarTrackerConfig = (trackerConfigId: string) => {
        const newPreset = trackerConfigs?.find((c) => c.id === trackerConfigId);
        const RadarTrackerConfig = trackerConfigId === emptyGuid ? radarConfig.radarTrackerConfig : newPreset?.trackerConfig ?? "";
        onChange({ ...radarConfig, radarTrackerConfigId: trackerConfigId === emptyGuid || !trackerConfigId ? undefined : trackerConfigId, radarTrackerConfigPreset: newPreset, radarTrackerConfig: RadarTrackerConfig } as Partial<RadarConfiguration>);
    };

    const syncTrackerToChirp = (type: "radar" | "video") => {
        if (type === "radar") {
            setNewRadarTrackerConfigId(radarConfig.chirpConfigPreset?.radarTrackerConfigId ?? "");
            updateRadarTrackerConfig(radarConfig.chirpConfigPreset?.radarTrackerConfigId ?? emptyGuid);
        } else {
            setNewVideoTrackerConfigId(radarConfig.chirpConfigPreset?.videoTrackerConfigId ?? "");
            updateVideoTrackerConfig(radarConfig.chirpConfigPreset?.videoTrackerConfigId ?? emptyGuid);
        }
    };

    const handleRadarTrackerOnChange: ChangeEventHandler<HTMLSelectElement> = async (e) => {
        setNewRadarTrackerConfigId(e.target.value ?? "");
        updateRadarTrackerConfig(e.target.value ?? "");
    };

    const handleVideoTrackerOnChange: ChangeEventHandler<HTMLSelectElement> = async (e) => {
        setNewVideoTrackerConfigId(e.target.value ?? "");
        updateVideoTrackerConfig(e.target.value ?? "");
    };

    return (
        <>
            {config.configType === SensorConfigType.Radar && (
                <>
                    <HeaderWithSeparator>Radar Configuration</HeaderWithSeparator>
                    <InputRow>
                        <CheckboxInput label="Enable Transmitter" checked={radarConfig.transmitterEnabled ?? true} onChange={radarHelpers.handlerFor("transmitterEnabled", onChange)} />
                    </InputRow>
                    <InputRow>
                        <Input label="Seconds to Transmit per Minute" {...radarHelpers.bindingsFor("secondsToTransmitPerMinute", onChange)} />
                    </InputRow>
                    <InputRow>
                        <Input label="Minimum Seconds to Transmit" {...radarHelpers.bindingsFor("minimumSecondsToTransmit", onChange)} />
                    </InputRow>
                    <InputRow>
                        {/* Factory calibration data — only super admins may edit it. Non-admins see the
                            current value read-only; the server also enforces this on save. */}
                        <Input label="Calibration" disabled={!isSuperAdmin} {...radarHelpers.bindingsFor("calibrationConfig", onChange)} />
                    </InputRow>

                    <InputRow>
                        <Select label="CHIRP Config" isLoadingOptions={!chirpOptionsReady} emptyOptionText={chirpOptionsReady ? "Device Default" : undefined} emptyOptionDisabled={false} options={chirpConfigOptions} onChange={handleChirpConfigOnChange} value={newChirpConfigId ?? ""} />
                    </InputRow>

                    {isSuperAdmin && newChirpConfigId === emptyGuid && (
                        <InputRow>
                            <TextArea rows={10} label="Custom CHIRP Config" {...radarHelpers.bindingsFor("chirpConfig", onChange)} />
                        </InputRow>
                    )}
                    {chirpConfigError && <InputRow><span className="text-red-500">{chirpConfigError}</span></InputRow>}
                    {/* Only a preset has anything to add (its description); for Custom / Device Default
                        this just repeated the select. */}
                    {newChirpConfigId && newChirpConfigId !== emptyGuid && <InputRow>{getChirpConfigDescription(radarConfig)}</InputRow>}

                    <HeaderWithSeparator>Tracker Configurations</HeaderWithSeparator>

                    <InputRow>
                        <Select label="Radar Tracker Config" isLoadingOptions={!trackerOptionsReady} emptyOptionText={trackerOptionsReady ? "Device Default" : undefined} emptyOptionDisabled={false} options={radarTrackerConfigOptions} onChange={handleRadarTrackerOnChange} value={newRadarTrackerConfigId ?? ""} />
                    </InputRow>
                    {isSuperAdmin && newRadarTrackerConfigId === emptyGuid && (
                        <InputRow>
                            <TextArea rows={5} label="Custom Radar Tracker Config" {...radarHelpers.bindingsFor("radarTrackerConfig", onChange)} />
                        </InputRow>
                    )}
                    <InputRow>
                        {getTrackerConfigDescription("radar")}
                        {!trackerConfigMatches("radar") && (
                            <>
                                {getTrackerConfigDescription("radar") && <br />}
                                <span className="text-red-500">
                                    This tracker config is not the default for the selected CHIRP.
                                    <br />
                                    <IconLink Icon={Icons.Refresh} onClick={() => syncTrackerToChirp("radar")}>
                                        Select default tracker config
                                    </IconLink>
                                </span>
                            </>
                        )}
                    </InputRow>

                    {isSuperAdmin && (
                        <>
                            <InputRow>
                                <Select label="Radar Classification" emptyOptionDisabled
                                    options={[
                                        { label: "Length", value: RadarClassificationMode.Length },
                                        { label: "Learned (falls back to Length until confident)", value: RadarClassificationMode.Learned },
                                    ]}
                                    value={radarConfig.radarClassificationMode ?? RadarClassificationMode.Length}
                                    onChange={(e) => onChange({ ...radarConfig, radarClassificationMode: Number(e.target.value) as RadarClassificationMode })} />
                            </InputRow>
                            <InputRow>
                                <div className="flex flex-row items-center gap-2">
                                    <CheckboxInput
                                        label="Freeze Detection Learning"
                                        checked={radarConfig.freezeDetectionProfileLearning ?? false}
                                        onChange={radarHelpers.handlerFor("freezeDetectionProfileLearning", onChange)} />
                                    <HoverTooltip
                                        show={freezeInfoOpen}
                                        placement="right"
                                        hoverable={
                                            <button type="button" className="flex items-center border-0 shadow-none bg-transparent p-0 m-0 h-auto focus:ring-0 focus-visible:ring-2" aria-label="What does Freeze Detection Learning do?" onClick={() => setFreezeInfoOpen((open) => !open)} onBlur={() => setFreezeInfoOpen(false)}>
                                                <Icons.Info className="h-4 w-4 text-brand-primary" />
                                            </button>
                                        }>
                                        <div className="space-y-2 text-sm">
                                            <p>
                                                The device normally learns what real vehicles look like at this site and, over time, adjusts the radar
                                                tracker's detection thresholds to suit it.
                                            </p>
                                            <p>
                                                Freezing stops that: no new learning, and no further automatic changes. Whatever thresholds are in the
                                                radar tracker config right now stay exactly as they are. Profiles already learned are kept.
                                            </p>
                                            <p>
                                                Use it when you've tuned the thresholds by hand (via a Custom Radar Tracker Config) and don't want
                                                them changed, or while the site's traffic isn't typical — construction, testing — so the device doesn't
                                                learn from it.
                                            </p>
                                        </div>
                                    </HoverTooltip>
                                </div>
                            </InputRow>
                            {radarConfig.learnedDetectionProfiles && (
                                <InputRow>
                                    <TextArea rows={10} disabled label="Learned Detection Profiles (uploaded by device)" value={radarConfig.learnedDetectionProfiles} />
                                </InputRow>
                            )}
                        </>
                    )}

                    <InputRow>
                        <Select label="Video Tracker Config" isLoadingOptions={!trackerOptionsReady} emptyOptionText={trackerOptionsReady ? "Device Default" : undefined} emptyOptionDisabled={false} options={videoTrackerConfigOptions} onChange={handleVideoTrackerOnChange} value={newVideoTrackerConfigId ?? ""} />
                    </InputRow>
                    {isSuperAdmin && newVideoTrackerConfigId === emptyGuid && (
                        <InputRow>
                            <TextArea rows={5} label="Custom Video Tracker Config" {...radarHelpers.bindingsFor("videoTrackerConfig", onChange)} />
                        </InputRow>
                    )}
                    <InputRow>
                        {getTrackerConfigDescription("video")}
                        {!trackerConfigMatches("video") && (
                            <>
                                {getTrackerConfigDescription("video") && <br />}
                                <span className="text-red-500">
                                    This tracker config is not the default for the selected CHIRP.
                                    <br />
                                    <IconLink Icon={Icons.Refresh} onClick={() => syncTrackerToChirp("video")}>
                                        Select default tracker config
                                    </IconLink>
                                </span>
                            </>
                        )}
                    </InputRow>
                </>
            )}
        </>
    );
};
